import { Chart, registerables } from 'chart.js';
import chartTheme from '../styles/chartTheme';
import React, { useEffect, useRef, useMemo, useState } from 'react';

Chart.register(...registerables);

const HOT_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#fb923c', '#fbbf24'];
const COLD_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#06b6d4', '#0ea5e9'];

export default function TrendsTab({ game, draws, sortedByFreq }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [customNum, setCustomNum] = useState('');
  const [trackedNums, setTrackedNums] = useState(null);

  const defaultTracked = useMemo(() => {
    if (!sortedByFreq.length) return [];
    const hot = sortedByFreq.slice(0, 3).map(e => e.n);
    const cold = sortedByFreq.slice(-3).map(e => e.n);
    return [...hot, ...cold];
  }, [sortedByFreq]);

  const tracked = trackedNums || defaultTracked;

  const { labels, datasets } = useMemo(() => {
    if (!draws.length || !tracked.length) return { labels: [], datasets: [] };

    const running = {};
    tracked.forEach(n => { running[n] = 0; });
    const cumulative = {};
    tracked.forEach(n => { cumulative[n] = []; });
    const dateLabels = [];

    draws.forEach((draw, i) => {
      tracked.forEach(n => {
        if (draw.main.includes(n)) running[n]++;
        cumulative[n].push(running[n]);
      });
      dateLabels.push(draw.date);
    });

    // Sample labels to avoid overcrowding (show ~60 labels max)
    const step = Math.max(1, Math.floor(dateLabels.length / 60));
    const sampledLabels = dateLabels.map((d, i) => {
      if (i % step !== 0) return '';
      const [, m, day] = d.split('-');
      return `${parseInt(m, 10)}/${parseInt(day, 10)}`;
    });

    const hotSet = new Set(defaultTracked.slice(0, 5));

    const ds = tracked.map((n, idx) => {
      const isHot = hotSet.has(n);
      const isCustom = trackedNums && !defaultTracked.includes(n);
      const colorArr = isHot ? HOT_COLORS : COLD_COLORS;
      const color = isCustom ? '#4ade80' : colorArr[idx % colorArr.length];
      return {
        label: isHot ? `🔥 #${n}` : isCustom ? `✦ #${n}` : `❄ #${n}`,
        data: cumulative[n],
        borderColor: color,
        backgroundColor: color + '20',
        borderWidth: isCustom ? 2.5 : 2.5,
        borderDash: isHot || isCustom ? [] : [6, 3],
        pointRadius: 0,
        tension: 0.3,
        fill: false,
      };
    });

    return { labels: sampledLabels, datasets: ds };
  }, [draws, tracked, defaultTracked, trackedNums]);

  useEffect(() => {
    if (!chartRef.current || !datasets.length) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const t = chartTheme();

    chartInstance.current = new Chart(chartRef.current, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            labels: { color: t.tick, font: { size: 11, family: t.fontFamily }, boxWidth: 12, padding: 10 },
          },
          tooltip: {
            callbacks: {
              title: (items) => items[0]?.label || '',
              label: (item) => `Ball #${item.dataset.label.slice(1)}: ${item.raw} times`,
            },
          },
        },
        scales: {
          x: {
            ticks: { font: { size: 9, family: t.fontFamily }, color: t.tick, maxRotation: 45, autoSkip: true, maxTicksLimit: 20 },
            grid: { display: false },
          },
          y: {
            ticks: { font: { size: 11, family: t.fontFamily }, color: t.tick },
            grid: { color: t.grid },
            title: { display: true, text: 'Cumulative draws', color: t.title, font: { size: 11, family: t.fontFamily } },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [labels, datasets]);

  const addCustomNumber = () => {
    const num = parseInt(customNum, 10);
    if (num >= 1 && num <= game.maxMain && !tracked.includes(num)) {
      setTrackedNums([...tracked, num]);
      setCustomNum('');
    }
  };

  const resetTracking = () => {
    setTrackedNums(null);
    setCustomNum('');
  };

  return (
    <>
      <p className="section-label">Cumulative frequency trends over time</p>
      <div className="trends-controls">
        <div className="trends-input-group">
          <input
            type="number"
            className="trends-input"
            placeholder={`Add # (1-${game.maxMain})`}
            min={1}
            max={game.maxMain}
            value={customNum}
            onChange={(e) => setCustomNum(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomNumber()}
          />
          <button className="mode-btn" onClick={addCustomNumber}>Add</button>
          {trackedNums && (
            <button className="mode-btn" onClick={resetTracking}>Reset</button>
          )}
        </div>
        <div className="trends-legend-hint">
          <span style={{ color: '#ef4444' }}>● Hot</span>
          <span style={{ color: '#3b82f6' }}>● Cold</span>
          {trackedNums && trackedNums.some(n => !defaultTracked.includes(n)) && (
            <span style={{ color: '#4ade80' }}>● Custom</span>
          )}
        </div>
      </div>
      <div className="chart-wrap trends-chart">
        <canvas ref={chartRef}></canvas>
      </div>
    </>
  );
}
